import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * Visit Store
 * Manages active visit/encounter state including:
 * - Visit status lifecycle (waiting → in-progress → paused → ended)
 * - Timing (start time, pause intervals, total/face-to-face seconds)
 * - Current CPT code for billing
 * - Timer interval management
 *
 * Used for visit phase tracking and billing calculations.
 */
export const useVisitStore = create(
  devtools(
    persist(
      (set, get) => ({
        // Visit state
        visitStatus: null, // 'waiting' | 'in-progress' | 'paused' | 'ended'
        // R3-2 — which FHIR Encounter this visit state belongs to. The
        // store persists to localStorage, so without this binding a
        // stale 'in-progress' visit from a PREVIOUS session suppressed
        // startVisit() for a brand-new encounter and the timer (and CPT
        // tier!) ran off the old startTime — 14h49m on a fresh visit in
        // the Round 3 smoke.
        encounterId: null,
        startTime: null, // ISO timestamp
        pauseTimes: [], // Array of { pausedAt, resumedAt }
        currentCptCode: null, // CPT code for current service
        timerInterval: null, // Interval ID for timer updates

        // Computed seconds
        totalSeconds: 0,
        faceToFaceSeconds: 0,

        // Visit actions

        /**
         * Starts a new visit/encounter
         */
        startVisit: (cptCode = null, encounterId = null) =>
          set(
            (state) => ({
              visitStatus: 'in-progress',
              encounterId,
              startTime: new Date().toISOString(),
              pauseTimes: [],
              currentCptCode: cptCode,
              totalSeconds: 0,
              faceToFaceSeconds: 0,
            }),
            false,
            'startVisit'
          ),

        /**
         * Pauses the active visit
         */
        pauseVisit: () =>
          set(
            (state) => {
              if (state.visitStatus !== 'in-progress') return state;

              return {
                visitStatus: 'paused',
                pauseTimes: [
                  ...state.pauseTimes,
                  {
                    pausedAt: new Date().toISOString(),
                    resumedAt: null,
                  },
                ],
              };
            },
            false,
            'pauseVisit'
          ),

        /**
         * Resumes a paused visit
         */
        resumeVisit: () =>
          set(
            (state) => {
              if (state.visitStatus !== 'paused') return state;

              const updatedPauseTimes = [...state.pauseTimes];
              if (updatedPauseTimes.length > 0) {
                updatedPauseTimes[updatedPauseTimes.length - 1].resumedAt = new Date().toISOString();
              }

              return {
                visitStatus: 'in-progress',
                pauseTimes: updatedPauseTimes,
              };
            },
            false,
            'resumeVisit'
          ),

        /**
         * Ends the visit and calculates final time metrics
         */
        endVisit: () =>
          set(
            (state) => {
              const endTime = new Date().toISOString();
              const { totalSeconds, faceToFaceSeconds } = calculateVisitDuration(
                state.startTime,
                state.pauseTimes,
                endTime
              );

              return {
                visitStatus: 'ended',
                totalSeconds,
                faceToFaceSeconds,
              };
            },
            false,
            'endVisit'
          ),

        /**
         * Updates CPT code for current visit
         */
        setCurrentCptCode: (cptCode) =>
          set(
            { currentCptCode: cptCode },
            false,
            'setCurrentCptCode'
          ),

        /**
         * Updates timer interval ID (for cleanup)
         */
        setTimerInterval: (intervalId) =>
          set(
            { timerInterval: intervalId },
            false,
            'setTimerInterval'
          ),

        /**
         * Clears timer interval
         */
        clearTimerInterval: () => {
          const { timerInterval } = get();
          if (timerInterval) {
            clearInterval(timerInterval);
          }
          set(
            { timerInterval: null },
            false,
            'clearTimerInterval'
          );
        },

        /**
         * Updates current timing metrics (called by timer)
         */
        updateTimingMetrics: () =>
          set(
            (state) => {
              if (!state.startTime || (state.visitStatus !== 'in-progress' && state.visitStatus !== 'paused')) {
                return state;
              }

              const { totalSeconds, faceToFaceSeconds } = calculateVisitDuration(
                state.startTime,
                state.pauseTimes,
                new Date().toISOString()
              );

              return {
                totalSeconds,
                faceToFaceSeconds,
              };
            },
            false,
            'updateTimingMetrics'
          ),

        /**
         * Resets visit store to initial state
         */
        resetVisit: () =>
          set(
            {
              visitStatus: null,
              encounterId: null,
              startTime: null,
              pauseTimes: [],
              currentCptCode: null,
              totalSeconds: 0,
              faceToFaceSeconds: 0,
              timerInterval: null,
            },
            false,
            'resetVisit'
          ),

        /**
         * Gets visit summary for documentation
         */
        getVisitSummary: () => {
          const state = get();
          return {
            status: state.visitStatus,
            startTime: state.startTime,
            totalSeconds: state.totalSeconds,
            faceToFaceSeconds: state.faceToFaceSeconds,
            cptCode: state.currentCptCode,
            pauseCount: state.pauseTimes.length,
          };
        },
      }),
      {
        name: 'visit-store',
        // v2 (R3-2): added encounterId binding
        version: 2,
        partialize: (state) => ({
          visitStatus: state.visitStatus,
          encounterId: state.encounterId,
          startTime: state.startTime,
          pauseTimes: state.pauseTimes,
          currentCptCode: state.currentCptCode,
          totalSeconds: state.totalSeconds,
          faceToFaceSeconds: state.faceToFaceSeconds,
        }),
      }
    )
  )
);

/**
 * Calculates total and face-to-face duration for a visit.
 *
 * Total seconds: entire visit duration from start to current
 * Face-to-face seconds: total minus all pause intervals
 *
 * @param {string} startTime - ISO timestamp of visit start
 * @param {Array} pauseTimes - Array of { pausedAt, resumedAt } objects
 * @param {string} endTime - ISO timestamp of current/end time
 * @returns {Object} { totalSeconds, faceToFaceSeconds }
 */
function calculateVisitDuration(startTime, pauseTimes, endTime) {
  if (!startTime) {
    return { totalSeconds: 0, faceToFaceSeconds: 0 };
  }

  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();
  const totalSeconds = Math.floor((end - start) / 1000);

  let pausedSeconds = 0;
  pauseTimes.forEach(({ pausedAt, resumedAt }) => {
    if (pausedAt && resumedAt) {
      const pauseStart = new Date(pausedAt).getTime();
      const pauseEnd = new Date(resumedAt).getTime();
      pausedSeconds += Math.floor((pauseEnd - pauseStart) / 1000);
    } else if (pausedAt && !resumedAt) {
      // Still paused
      const pauseStart = new Date(pausedAt).getTime();
      pausedSeconds += Math.floor((end - pauseStart) / 1000);
    }
  });

  const faceToFaceSeconds = Math.max(0, totalSeconds - pausedSeconds);

  return { totalSeconds, faceToFaceSeconds };
}
